import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Link as MuiLink,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthShell } from './AuthShell';
import { completeSetup } from '../../lib/auth/passwordClient';
import { PW_RULES } from '../../components/PasswordRequirements';

export function GettingStarted() {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const code = searchParams.get('code');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const ruleState = useMemo(() => PW_RULES.map((r) => ({ ...r, ok: r.test(password) })), [password]);
  const allRulesPass = ruleState.every((r) => r.ok);
  const passwordsMatch = password.length > 0 && password === confirm;

  if (!userId || !code) {
    return (
      <AuthShell>
        <Alert severity="error">
          This invite link is missing required parameters. Please ask an administrator to resend the invite.
        </Alert>
      </AuthShell>
    );
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!userId || !code) return; // narrowed above; keeps TS happy
    if (!allRulesPass) {
      setError('Please choose a password that meets all the requirements below.');
      return;
    }
    if (!passwordsMatch) {
      setError('The two passwords do not match.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // First/last name aren't asked for here — we already hold the entrant's
      // name from their entry, and the backend keeps it when none is sent.
      await completeSetup(userId, code, password);
      navigate('/login?welcome=1', { replace: true });
    } catch (err) {
      setError(
        extractErrorMessage(err) ??
          'Unable to complete setup. The invite may have expired — ask an administrator to resend it.'
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell>
      <Stack spacing={3}>
        <Stack spacing={0.5}>
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
          >
            Welcome to WAF
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Set a password to activate your account and access the portal.
          </Typography>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              label="Create password"
              type="password"
              autoComplete="new-password"
              required
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />

            <Box>
              <Typography variant="caption" color="text.secondary">
                Your password must contain:
              </Typography>
              <List dense disablePadding>
                {ruleState.map((r) => (
                  <ListItem key={r.label} disableGutters disablePadding sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {r.ok ? (
                        <CheckCircleIcon fontSize="small" color="success" />
                      ) : (
                        <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={r.label}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: r.ok ? 'text.primary' : 'text.secondary',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <TextField
              label="Confirm password"
              type="password"
              autoComplete="new-password"
              required
              fullWidth
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={submitting}
              error={confirm.length > 0 && !passwordsMatch}
              helperText={confirm.length > 0 && !passwordsMatch ? 'Passwords do not match.' : ' '}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting || !allRulesPass || !passwordsMatch}
            >
              {submitting ? 'Finishing…' : 'Finish setup'}
            </Button>
          </Stack>
        </Box>

        <Typography variant="body2" fontWeight={700} align="center" color="text.secondary">
          If you have any problems with this page, please email{' '}
          <MuiLink href="mailto:waf.customercare@emap.com" fontWeight={700} underline="hover">
            waf.customercare@emap.com
          </MuiLink>
        </Typography>
      </Stack>
    </AuthShell>
  );
}

function extractErrorMessage(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null;
  const maybe = err as { response?: { data?: { message?: string; error?: string } } };
  return maybe.response?.data?.message ?? maybe.response?.data?.error ?? null;
}
